/**
 * SellerWallet.jsx — CashIn-style
 *
 * Flow:
 *   Seller sees all coin balances (USDT, BTC, SOL etc)
 *   Seller taps a coin → selects how much to cash out
 *   Seller picks receive currency (NGN, GHS, KES etc)
 *   Seller enters bank details
 *   Submits → money lands in bank as local currency
 *
 * Just like CashIn — send crypto, receive Naira (or any currency) directly.
 */

import { useState, useEffect } from 'react';
import {
  ArrowDownToLine, Clock, CheckCircle, XCircle,
  RefreshCw, ChevronDown, ChevronRight, ArrowRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

// ── Constants ────────────────────────────────────────────────────────────────
const COINS = ['USDT','BTC','ETH','BNB','SOL','SUI','XRP','HYPE','ASTER'];

const COIN_CFG = {
  USDT:  { color:'#26A17B', bg:'rgba(38,161,123,0.12)',  label:'Tether',         stable:true  },
  BTC:   { color:'#F7931A', bg:'rgba(247,147,26,0.12)',  label:'Bitcoin',        stable:false },
  ETH:   { color:'#627EEA', bg:'rgba(98,126,234,0.12)',  label:'Ethereum',       stable:false },
  BNB:   { color:'#F0B90B', bg:'rgba(240,185,11,0.12)',  label:'BNB',            stable:false },
  SOL:   { color:'#9945FF', bg:'rgba(153,69,255,0.12)',  label:'Solana',         stable:false },
  SUI:   { color:'#4DA2FF', bg:'rgba(77,162,255,0.12)',  label:'Sui',            stable:false },
  XRP:   { color:'#0052FF', bg:'rgba(0,82,255,0.12)',    label:'XRP',            stable:false },
  HYPE:  { color:'#E040FB', bg:'rgba(224,64,251,0.12)',  label:'HyperLiquid',    stable:false },
  ASTER: { color:'#FF6B00', bg:'rgba(255,107,0,0.12)',   label:'Aster',          stable:false },
};

const FIAT_GROUPS = {
  '🌍 Africa':      ['NGN','GHS','KES','ZAR','TZS','UGX','ETB','EGP','MAD'],
  '🌎 Americas':    ['USD','CAD','MXN','BRL','ARS','CLP','COP'],
  '🌍 Europe':      ['EUR','GBP','CHF','NOK','SEK','DKK','PLN','TRY'],
  '🌏 Asia':        ['CNY','JPY','KRW','INR','IDR','THB','PHP','MYR','SGD'],
  '🕌 Middle East': ['AED','SAR','QAR','KWD','BHD','ILS'],
  '🌏 Oceania':     ['AUD','NZD'],
};

const FIAT_SYMBOLS = {
  NGN:'₦', GHS:'₵', KES:'KSh', ZAR:'R', USD:'$', EUR:'€', GBP:'£',
  TZS:'TSh', UGX:'USh', ETB:'Br', AED:'د.إ', SAR:'﷼', INR:'₹',
  CNY:'¥', JPY:'¥', KRW:'₩', AUD:'A$', CAD:'C$',
};

const STATUS_CFG = {
  pending:    { color:'#F5A623', Icon:Clock,        label:'Pending'    },
  processing: { color:'#4DA2FF', Icon:RefreshCw,    label:'Processing' },
  completed:  { color:'#00C864', Icon:CheckCircle,  label:'Paid ✓'     },
  failed:     { color:'#FF3232', Icon:XCircle,      label:'Failed'     },
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function SellerWallet() {
  const [screen, setScreen]         = useState('home');   // home | cashout | bank | confirm | history
  const [walletData, setWalletData] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [openRegion, setOpenRegion] = useState('🌍 Africa');

  // CashOut form state
  const [selectedCoin, setSelectedCoin] = useState('USDT');
  const [amount, setAmount]             = useState('');
  const [fiatCurrency, setFiatCurrency] = useState('NGN');
  const [bankDetails, setBankDetails]   = useState({
    bankName:'', accountNumber:'', accountName:'', swiftCode:'', iban:'', country:''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [w, wh] = await Promise.all([
        api.get('/seller/wallet'),
        api.get('/seller/wallet/withdrawals'),
      ]);
      setWalletData(w.data);
      setWithdrawals(wh.data.withdrawals || []);
    } catch {} finally { setLoading(false); }
  };

  const submitCashOut = async () => {
    if (!bankDetails.bankName || !bankDetails.accountNumber || !bankDetails.accountName) {
      return toast.error('Bank name, account number and account name are required');
    }
    setSubmitting(true);
    try {
      const res = await api.post('/seller/wallet/withdraw-to-bank', {
        amountUSDT:   parseFloat(amount),
        cryptoCoin:   selectedCoin,
        fiatCurrency,
        bankDetails,
      });
      toast.success(res.data.message || 'Withdrawal submitted successfully');
      setScreen('history');
      setAmount('');
      setBankDetails({ bankName:'', accountNumber:'', accountName:'', swiftCode:'', iban:'', country:'' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit withdrawal');
    } finally { setSubmitting(false); }
  };

  // ── Derived values ──────────────────────────────────────────────────────────
  const allBalances  = walletData?.wallet?.allBalances  || {};
  const coinUSDValues = walletData?.wallet?.coinUSDValues || {};
  const fiatEquiv    = walletData?.fiatEquivalents       || {};
  const totalUSD     = walletData?.wallet?.totalUSD      || 0;
  const totalEarned  = walletData?.totalEarnedUSDT       || 0;

  const coinBalance  = parseFloat(allBalances[selectedCoin] || 0);
  const coinUSD      = parseFloat(coinUSDValues[selectedCoin] || 0);
  const amtNum       = parseFloat(amount) || 0;
  // Estimated fiat for entered amount
  const usdPerCoin   = coinBalance > 0 ? coinUSD / coinBalance : 1;
  const amtUSD       = amtNum * usdPerCoin;
  const fiatRate     = fiatEquiv[fiatCurrency]
    ? fiatEquiv[fiatCurrency] / Math.max(totalUSD, 0.01)
    : 1;
  const estimatedFiat = parseFloat((amtUSD * fiatRate).toFixed(2));
  const fiatSym      = FIAT_SYMBOLS[fiatCurrency] || fiatCurrency + ' ';

  if (loading) return (
    <div className="flex justify-center items-center min-h-screen">
      <div className="spinner" />
    </div>
  );

  // ════════════════════════════════════════════════════════════
  // SCREEN: HOME — wallet overview
  // ════════════════════════════════════════════════════════════
  if (screen === 'home') return (
    <div className="min-h-screen pt-16 pb-28 px-4 max-w-lg mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between mt-4 mb-5">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily:'Sora' }}>My Wallet</h1>
          <p className="text-xs" style={{ color:'#6B6B8A' }}>Sell crypto · Receive in your bank</p>
        </div>
        <button onClick={() => setScreen('history')}
          className="text-xs font-semibold px-3 py-1.5 rounded-xl"
          style={{ background:'#12121A', border:'1px solid #1E1E2E', color:'#6B6B8A' }}>
          History
        </button>
      </div>

      {/* Total balance hero */}
      <div className="rounded-3xl p-5 mb-4 relative overflow-hidden"
        style={{ background:'linear-gradient(135deg,#0D1F14,#061018)', border:'1px solid rgba(38,161,123,0.35)' }}>
        <div style={{ position:'absolute', top:-30, right:-30, width:120, height:120, borderRadius:'50%', background:'rgba(38,161,123,0.08)' }} />
        <p className="text-xs font-semibold mb-1" style={{ color:'rgba(38,161,123,0.8)' }}>Total Balance</p>
        <div className="flex items-end gap-2 mb-1">
          <span className="text-4xl font-black text-white" style={{ fontFamily:'Sora' }}>
            ${totalUSD.toFixed(2)}
          </span>
          <span className="text-sm mb-1" style={{ color:'#26A17B' }}>USD</span>
        </div>
        {/* Fiat equivalents */}
        <div className="flex gap-2 flex-wrap mt-2">
          {Object.entries(fiatEquiv).slice(0,3).map(([code, val]) => (
            <span key={code} className="text-xs px-2 py-0.5 rounded-lg font-mono"
              style={{ background:'rgba(38,161,123,0.15)', color:'#26A17B' }}>
              {FIAT_SYMBOLS[code]||code+' '}{val.toLocaleString()}
            </span>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="p-3 rounded-2xl" style={{ background:'#12121A', border:'1px solid #1E1E2E' }}>
          <p className="text-xs mb-1" style={{ color:'#6B6B8A' }}>Total Earned</p>
          <p className="text-lg font-black" style={{ color:'#26A17B', fontFamily:'Sora' }}>
            ${totalEarned.toFixed(2)}
          </p>
        </div>
        <div className="p-3 rounded-2xl" style={{ background:'#12121A', border:'1px solid #1E1E2E' }}>
          <p className="text-xs mb-1" style={{ color:'#6B6B8A' }}>Withdrawals</p>
          <p className="text-lg font-black" style={{ color:'#FF6B00', fontFamily:'Sora' }}>
            {withdrawals.length}
          </p>
        </div>
      </div>

      {/* Coin list */}
      <p className="text-xs font-bold mb-3" style={{ color:'#6B6B8A', letterSpacing:'1.5px', textTransform:'uppercase' }}>
        Your Coins
      </p>

      <div className="space-y-2 mb-6">
        {COINS.map(coin => {
          const bal = parseFloat(allBalances[coin] || 0);
          const usd = parseFloat(coinUSDValues[coin] || (coin === 'USDT' ? bal : 0));
          const cfg = COIN_CFG[coin];
          if (bal <= 0 && coin !== 'USDT') return null;
          return (
            <button key={coin}
              onClick={() => { setSelectedCoin(coin); setAmount(''); setScreen('cashout'); }}
              className="w-full flex items-center gap-3 p-3.5 rounded-2xl transition-all active:scale-98"
              style={{ background: coin==='USDT' ? 'rgba(38,161,123,0.07)' : '#12121A',
                       border:`1px solid ${coin==='USDT' ? 'rgba(38,161,123,0.3)' : '#1E1E2E'}` }}>
              {/* Coin icon */}
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center font-black text-xs flex-shrink-0"
                style={{ background:cfg.bg, color:cfg.color, fontFamily:'JetBrains Mono' }}>
                {coin.slice(0,3)}
              </div>
              {/* Coin info */}
              <div style={{ flex:1, textAlign:'left' }}>
                <div className="font-bold text-sm text-white">{cfg.label}</div>
                <div className="text-xs font-mono" style={{ color:cfg.color }}>
                  {coin === 'BTC' ? bal.toFixed(8) : bal.toFixed(4)} {coin}
                </div>
              </div>
              {/* USD value */}
              <div style={{ textAlign:'right' }}>
                <div className="font-bold text-sm text-white">${usd.toFixed(2)}</div>
                {cfg.stable && <div className="text-xs" style={{ color:'#26A17B' }}>Stable</div>}
              </div>
              {/* Arrow */}
              <ChevronRight size={16} style={{ color:'#6B6B8A' }} />
            </button>
          );
        })}

        {COINS.every(c => !allBalances[c] || allBalances[c] <= 0) && (
          <div className="text-center py-10" style={{ color:'#6B6B8A' }}>
            <p className="text-sm">No coins yet</p>
            <p className="text-xs mt-1">Complete your first sale to earn</p>
          </div>
        )}
      </div>

      {/* Cash Out CTA */}
      <button onClick={() => { setSelectedCoin('USDT'); setAmount(''); setScreen('cashout'); }}
        className="w-full py-4 rounded-2xl font-bold text-white flex items-center justify-center gap-2"
        style={{ background:'linear-gradient(135deg,#26A17B,#00C864)', fontSize:16 }}>
        <ArrowDownToLine size={18} />
        Cash Out to Bank
      </button>
    </div>
  );

  // ════════════════════════════════════════════════════════════
  // SCREEN: CASHOUT — pick coin and amount
  // ════════════════════════════════════════════════════════════
  if (screen === 'cashout') {
    const cfg = COIN_CFG[selectedCoin];
    const bal = parseFloat(allBalances[selectedCoin] || 0);
    const usd = parseFloat(coinUSDValues[selectedCoin] || (selectedCoin === 'USDT' ? bal : 0));

    return (
      <div className="min-h-screen pt-16 pb-28 px-4 max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mt-4 mb-6">
          <button onClick={() => setScreen('home')}
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background:'#12121A', border:'1px solid #1E1E2E' }}>
            ←
          </button>
          <div>
            <h2 className="text-xl font-bold" style={{ fontFamily:'Sora' }}>Cash Out</h2>
            <p className="text-xs" style={{ color:'#6B6B8A' }}>Send crypto · Receive in your bank</p>
          </div>
        </div>

        {/* Coin selector */}
        <p className="text-xs font-bold mb-2" style={{ color:'#6B6B8A', letterSpacing:'1.5px', textTransform:'uppercase' }}>
          Select Coin
        </p>
        <div className="flex gap-2 overflow-x-auto pb-2 mb-5" style={{ scrollbarWidth:'none' }}>
          {COINS.map(coin => {
            const c = COIN_CFG[coin];
            const b = parseFloat(allBalances[coin] || 0);
            return (
              <button key={coin} onClick={() => { setSelectedCoin(coin); setAmount(''); }}
                className="flex-shrink-0 px-4 py-2 rounded-xl font-bold text-xs transition-all"
                style={{
                  background:  selectedCoin===coin ? c.color : '#12121A',
                  color:       selectedCoin===coin ? 'white'  : c.color,
                  border:      `1px solid ${selectedCoin===coin ? c.color : '#1E1E2E'}`,
                  opacity:     b <= 0 ? 0.45 : 1,
                  fontFamily:  'JetBrains Mono',
                }}>
                {coin}
              </button>
            );
          })}
        </div>

        {/* Selected coin balance */}
        <div className="rounded-2xl p-4 mb-5"
          style={{ background:cfg.bg, border:`1px solid ${cfg.color}44` }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs mb-0.5" style={{ color:cfg.color }}>Available {selectedCoin}</p>
              <p className="text-2xl font-black text-white" style={{ fontFamily:'Sora' }}>
                {selectedCoin === 'BTC' ? bal.toFixed(8) : bal.toFixed(4)} {selectedCoin}
              </p>
              <p className="text-sm" style={{ color:cfg.color }}>≈ ${usd.toFixed(2)} USD</p>
            </div>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center font-black text-sm"
              style={{ background:`${cfg.color}22`, color:cfg.color, fontFamily:'JetBrains Mono' }}>
              {selectedCoin.slice(0,3)}
            </div>
          </div>
        </div>

        {/* Amount input */}
        <div className="rounded-2xl p-4 mb-4" style={{ background:'#12121A', border:'1px solid #1E1E2E' }}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-white">Amount to Cash Out</p>
            <button onClick={() => setAmount(bal.toFixed(selectedCoin === 'BTC' ? 8 : 4))}
              className="text-xs font-bold px-2 py-0.5 rounded-lg"
              style={{ background:`${cfg.color}22`, color:cfg.color }}>
              MAX
            </button>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              placeholder={`0.00 ${selectedCoin}`}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              className="input-dark flex-1 text-xl font-bold"
              style={{ fontFamily:'JetBrains Mono', color:cfg.color }}
              min="0"
              max={bal}
              step={selectedCoin === 'BTC' ? '0.00001' : '0.01'}
            />
            <span className="font-bold text-sm" style={{ color:cfg.color }}>{selectedCoin}</span>
          </div>
          {amtNum > 0 && (
            <p className="text-xs mt-2" style={{ color:'#6B6B8A' }}>
              ≈ ${amtUSD.toFixed(2)} USD
            </p>
          )}
        </div>

        {/* Receive currency */}
        <div className="rounded-2xl p-4 mb-5" style={{ background:'#12121A', border:'1px solid #1E1E2E' }}>
          <p className="text-xs font-semibold text-white mb-3">Receive Currency</p>
          {/* Africa note */}
          <div style={{ background:'rgba(0,200,100,0.06)', border:'1px solid rgba(0,200,100,0.2)', borderRadius:10, padding:'8px 10px', marginBottom:10, fontSize:11, color:'#A0A0B8', lineHeight:1.5 }}>
            <span style={{ color:'#00C864', fontWeight:700 }}>🌍 Africa currencies</span> (NGN, GHS, KES, ZAR, UGX etc) are paid automatically via <span style={{ color:'#26A17B', fontWeight:700 }}>Flutterwave</span> — usually arrives in minutes.<br/>
            Other currencies are processed manually within 1-3 business days.
          </div>
          {/* Selected */}
          <div className="flex items-center gap-2 p-2.5 rounded-xl mb-3"
            style={{ background:'rgba(38,161,123,0.08)', border:'1px solid rgba(38,161,123,0.3)' }}>
            <span className="font-black text-sm font-mono" style={{ color:'#26A17B' }}>{fiatCurrency}</span>
            <span className="text-xs" style={{ color:'#6B6B8A' }}>
              {amtNum > 0 ? `≈ ${fiatSym}${estimatedFiat.toLocaleString()}` : 'Enter amount to see estimate'}
            </span>
          </div>
          {/* Region picker */}
          <div className="space-y-1 max-h-44 overflow-y-auto" style={{ scrollbarWidth:'none' }}>
            {Object.entries(FIAT_GROUPS).map(([region, codes]) => (
              <div key={region} className="rounded-xl overflow-hidden" style={{ border:'1px solid #1E1E2E' }}>
                <button onClick={() => setOpenRegion(openRegion===region ? null : region)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold"
                  style={{ background:'#0A0A0F', color:'#6B6B8A' }}>
                  <span>{region}</span>
                  <ChevronDown size={11} className={openRegion===region ? 'rotate-180' : ''} style={{ transition:'transform .2s' }} />
                </button>
                {openRegion === region && (
                  <div className="grid grid-cols-4 gap-1 p-2" style={{ background:'#0A0A0F' }}>
                    {codes.map(code => (
                      <button key={code} onClick={() => setFiatCurrency(code)}
                        className="py-1.5 rounded-lg text-xs font-bold font-mono transition-all"
                        style={{
                          background: fiatCurrency===code ? 'rgba(38,161,123,0.2)' : '#12121A',
                          color:      fiatCurrency===code ? '#26A17B' : '#6B6B8A',
                          border:    `1px solid ${fiatCurrency===code ? '#26A17B' : '#1E1E2E'}`,
                        }}>
                        {code}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Preview pill */}
        {amtNum > 0 && (
          <div className="flex items-center justify-between p-3 rounded-2xl mb-5"
            style={{ background:'rgba(38,161,123,0.07)', border:'1px solid rgba(38,161,123,0.25)' }}>
            <div className="text-center">
              <p className="text-xs" style={{ color:'#6B6B8A' }}>You send</p>
              <p className="font-black text-sm" style={{ color:cfg.color, fontFamily:'JetBrains Mono' }}>
                {amtNum} {selectedCoin}
              </p>
            </div>
            <ArrowRight size={18} style={{ color:'#26A17B' }} />
            <div className="text-center">
              <p className="text-xs" style={{ color:'#6B6B8A' }}>Bank receives</p>
              <p className="font-black text-sm" style={{ color:'#26A17B' }}>
                {fiatSym}{estimatedFiat.toLocaleString()} {fiatCurrency}
              </p>
            </div>
          </div>
        )}

        {/* Payout Network Info */}
        <div style={{ background:'rgba(38,161,123,0.08)', border:'1px solid rgba(38,161,123,0.3)', borderRadius:16, padding:'12px 14px', marginBottom:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
            <span style={{ fontSize:16 }}>⚡</span>
            <span style={{ fontSize:12, fontWeight:700, color:'#26A17B' }}>Polygon USDT — Recommended</span>
            <span style={{ fontSize:10, background:'rgba(38,161,123,0.2)', color:'#26A17B', borderRadius:6, padding:'2px 6px', fontWeight:700 }}>BEST</span>
          </div>
          <div style={{ fontSize:11, color:'#A0A0B8', lineHeight:1.5 }}>
            Fastest settlement · Cheapest gas (~$0.01) · Supported by Flutterwave for Africa payouts. Your funds arrive in Naira, Cedis or any local currency directly to your bank.
          </div>
        </div>

        <button
          onClick={() => {
            if (!amount || amtNum <= 0) return toast.error('Enter an amount');
            if (amtNum > bal) return toast.error(`You only have ${bal} ${selectedCoin}`);
            setScreen('bank');
          }}
          className="w-full py-4 rounded-2xl font-bold text-white flex items-center justify-center gap-2"
          style={{ background:'linear-gradient(135deg,#26A17B,#00C864)', fontSize:15, opacity: amtNum > 0 ? 1 : 0.5 }}>
          Continue → Enter Bank Details
        </button>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════
  // SCREEN: BANK — enter bank details
  // ════════════════════════════════════════════════════════════
  if (screen === 'bank') {
    const cfg = COIN_CFG[selectedCoin];
    return (
      <div className="min-h-screen pt-16 pb-28 px-4 max-w-lg mx-auto">
        <div className="flex items-center gap-3 mt-4 mb-6">
          <button onClick={() => setScreen('cashout')}
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background:'#12121A', border:'1px solid #1E1E2E' }}>
            ←
          </button>
          <div>
            <h2 className="text-xl font-bold" style={{ fontFamily:'Sora' }}>Bank Details</h2>
            <p className="text-xs" style={{ color:'#6B6B8A' }}>Where should we send your {fiatCurrency}?</p>
          </div>
        </div>

        {/* Summary pill */}
        <div className="flex items-center justify-between p-3.5 rounded-2xl mb-5"
          style={{ background:'#12121A', border:'1px solid #1E1E2E' }}>
          <div>
            <p className="text-xs" style={{ color:'#6B6B8A' }}>Sending</p>
            <p className="font-black text-sm" style={{ color:cfg.color, fontFamily:'JetBrains Mono' }}>
              {amount} {selectedCoin}
            </p>
          </div>
          <ArrowRight size={16} style={{ color:'#6B6B8A' }} />
          <div style={{ textAlign:'right' }}>
            <p className="text-xs" style={{ color:'#6B6B8A' }}>Arriving as</p>
            <p className="font-black text-sm" style={{ color:'#26A17B' }}>
              {fiatSym}{estimatedFiat.toLocaleString()} {fiatCurrency}
            </p>
          </div>
        </div>

        {/* Bank form */}
        <div className="space-y-3 mb-5">
          {[
            { key:'bankName',      ph:'Bank Name *',              req:true  },
            { key:'accountNumber', ph:'Account Number *',         req:true  },
            { key:'accountName',   ph:'Account Name *',           req:true  },
            { key:'country',       ph:'Country',                  req:false },
            { key:'swiftCode',     ph:'SWIFT / BIC (international)',req:false },
            { key:'iban',          ph:'IBAN (Europe only)',        req:false },
          ].map(({ key, ph, req }) => (
            <div key={key}>
              <input
                className="input-dark w-full"
                placeholder={ph}
                value={bankDetails[key]}
                onChange={e => setBankDetails({ ...bankDetails, [key]: e.target.value })}
              />
            </div>
          ))}
        </div>

        {/* Note */}
        <div className="p-3 rounded-xl text-xs mb-5" style={{ background:'#0A0A0F', border:'1px solid #1E1E2E', color:'#6B6B8A' }}>
          🌍 Africa payouts (NGN/GHS/KES/ZAR/UGX etc): Automatic via Flutterwave · Other regions: Manual processing 1-3 days · Min $10 equivalent
        </div>

        <button onClick={submitCashOut} disabled={submitting}
          className="w-full py-4 rounded-2xl font-bold text-white flex items-center justify-center gap-2 disabled:opacity-40"
          style={{ background:'linear-gradient(135deg,#26A17B,#00C864)', fontSize:15 }}>
          {submitting
            ? <><div className="spinner" /> Processing...</>
            : <><ArrowDownToLine size={18} /> Cash Out {amount} {selectedCoin} → {fiatCurrency}</>
          }
        </button>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════
  // SCREEN: HISTORY — withdrawal history
  // ════════════════════════════════════════════════════════════
  if (screen === 'history') return (
    <div className="min-h-screen pt-16 pb-28 px-4 max-w-lg mx-auto">
      <div className="flex items-center gap-3 mt-4 mb-6">
        <button onClick={() => setScreen('home')}
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background:'#12121A', border:'1px solid #1E1E2E' }}>
          ←
        </button>
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily:'Sora' }}>Cash Out History</h2>
          <p className="text-xs" style={{ color:'#6B6B8A' }}>{withdrawals.length} transactions</p>
        </div>
      </div>

      {withdrawals.length === 0 ? (
        <div className="text-center py-20" style={{ color:'#6B6B8A' }}>
          <ArrowDownToLine size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No cash outs yet</p>
          <p className="text-xs mt-1">Your withdrawal history will appear here</p>
          <button onClick={() => setScreen('cashout')}
            className="mt-4 px-5 py-2.5 rounded-xl font-bold text-white text-sm"
            style={{ background:'linear-gradient(135deg,#26A17B,#00C864)' }}>
            Cash Out Now
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {withdrawals.map(w => {
            const cfg = STATUS_CFG[w.status] || STATUS_CFG.pending;
            const Icon = cfg.Icon;
            const coinCfg = COIN_CFG[w.cryptoCurrency] || COIN_CFG.USDT;
            return (
              <div key={w._id} className="p-4 rounded-2xl" style={{ background:'#12121A', border:'1px solid #1E1E2E' }}>
                {/* Top row */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center font-black text-xs"
                      style={{ background:coinCfg.bg, color:coinCfg.color, fontFamily:'JetBrains Mono' }}>
                      {(w.cryptoCurrency||'USDT').slice(0,3)}
                    </div>
                    <div>
                      <p className="font-black text-sm" style={{ color:coinCfg.color, fontFamily:'JetBrains Mono' }}>
                        {w.amountCrypto} {w.cryptoCurrency || 'USDT'}
                      </p>
                      <p className="text-xs" style={{ color:'#6B6B8A' }}>
                        {new Date(w.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs font-bold" style={{ color:cfg.color }}>
                    <Icon size={11} /> {cfg.label}
                  </div>
                </div>
                {/* Amount received */}
                <div className="flex items-center justify-between p-2.5 rounded-xl"
                  style={{ background:'#0A0A0F', border:'1px solid #1E1E2E' }}>
                  <span className="text-xs" style={{ color:'#6B6B8A' }}>Bank receives</span>
                  <span className="font-black text-sm" style={{ color:'#26A17B' }}>
                    {FIAT_SYMBOLS[w.fiatCurrency]||w.fiatCurrency+' '}
                    {w.fiatAmount?.toLocaleString() || '—'} {w.fiatCurrency}
                  </span>
                </div>
                {/* Bank info */}
                <p className="text-xs mt-2" style={{ color:'#6B6B8A' }}>
                  {w.bankDetails?.bankName} · ****{w.bankDetails?.accountNumber?.slice(-4)}
                </p>
                {w.bankReference && (
                  <p className="text-xs font-mono mt-1" style={{ color:'#00C864' }}>Ref: {w.bankReference}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return null;
}
